import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Business,
  People,
  Message,
  Settings,
  Visibility,
  Block,
  CheckCircle,
  Refresh,
  CloudUpload,
} from '@mui/icons-material';
import { DataTable } from '../common';
import apiService from '../../services/api';
import authService from '../../services/auth';

const TenantManagement = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('view'); // 'view', 'edit', 'create'

  // Filtered tenants based on search
  const filteredTenants = useMemo(() => {
    if (!searchTerm) return tenants;
    
    return tenants.filter(tenant => 
      tenant.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tenant.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tenant.phone?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [tenants, searchTerm]);

  // DataTable columns configuration
  const columns = [
    {
      field: 'name',
      header: 'Tenant Name',
      type: 'text',
      sortable: true,
      exportable: true,
      exportWidth: 30,
    },
    {
      field: 'email',
      header: 'Email',
      type: 'text',
      sortable: true,
      exportable: true,
      exportWidth: 25,
    },
    {
      field: 'phone',
      header: 'Phone',
      type: 'text',
      sortable: true,
      exportable: true,
      exportWidth: 15,
    },
    {
      field: 'status',
      header: 'Status',
      type: 'status',
      sortable: true,
      exportable: true,
      exportWidth: 15,
    },
    {
      field: 'created_at',
      header: 'Created',
      type: 'date',
      sortable: true,
      exportable: true,
      exportWidth: 15,
    },
    {
      field: 'actions',
      header: 'Actions',
      type: 'actions',
      sortable: false,
      exportable: false,
    },
  ];

  // DataTable actions configuration
  const actions = [
    {
      type: 'view',
      label: 'View',
      icon: <Visibility sx={{ mr: 1 }} />,
    },
    {
      type: 'upload_services',
      label: 'Upload Services',
      icon: <CloudUpload sx={{ mr: 1 }} />,
    },
    {
      type: 'edit',
      label: 'Edit',
      icon: <Edit sx={{ mr: 1 }} />,
    },
    {
      type: 'delete',
      label: 'Delete',
      icon: <Delete sx={{ mr: 1 }} />,
    },
  ];

  // Status options for status display
  const statusOptions = [
    { value: 'active', label: 'Active', color: 'success' },
    { value: 'inactive', label: 'Inactive', color: 'error' },
    { value: 'suspended', label: 'Suspended', color: 'warning' },
  ];

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      console.log('TenantManagement: Fetching tenants...');
      console.log('TenantManagement: Current user:', authService.getUser());
      console.log('TenantManagement: Session token:', authService.getSessionToken());
      
      const response = await apiService.get('/superadmin/tenants');
      console.log('TenantManagement: API response:', response);
      
      if (response.success) {
        setTenants(response.data);
      } else {
        setError(response.message || 'Failed to fetch tenants');
      }
    } catch (err) {
      console.error('TenantManagement: Error details:', err);
      setError('Error fetching tenants: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = () => {
    setSelectedTenant(null);
    setDialogMode('create');
    setDialogOpen(true);
  };

  const handleEditTenant = (tenant) => {
    setSelectedTenant(tenant);
    setDialogMode('edit');
    setDialogOpen(true);
  };

  // DataTable action handler
  const handleTableAction = (action, rowData) => {
    switch (action) {
      case 'view':
        handleViewTenant(rowData);
        break;
      case 'upload_services':
        navigate(`/superadmin/tenants/${rowData.id}/upload-services`);
        break;
      case 'edit':
        handleEditTenant(rowData);
        break;
      case 'delete':
        handleDeleteTenant(rowData);
        break;
      default:
        console.log('Unknown action:', action);
    }
  };

  const handleViewTenant = (tenant) => {
    setSelectedTenant(tenant);
    setDialogMode('view');
    setDialogOpen(true);
  };

  const handleDeleteTenant = async (tenantId) => {
    if (!window.confirm('Are you sure you want to delete this tenant? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await apiService.delete(`/superadmin/tenants/${tenantId}`);
      if (response.success) {
        fetchTenants();
      } else {
        setError(response.message || 'Failed to delete tenant');
      }
    } catch (err) {
      setError('Error deleting tenant: ' + err.message);
    }
  };

  const handleToggleStatus = async (tenantId, currentStatus) => {
    try {
      const response = await apiService.put(`/superadmin/tenants/${tenantId}/status`, {
        status: currentStatus === 'active' ? 'inactive' : 'active'
      });
      if (response.success) {
        fetchTenants();
      } else {
        setError(response.message || 'Failed to update tenant status');
      }
    } catch (err) {
      setError('Error updating tenant status: ' + err.message);
    }
  };



  const getStatusColor = (status) => {
    return status === 'active' ? 'success' : 'error';
  };



  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold" color="primary.main">
          Tenant Management
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchTenants}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateTenant}
          >
            Add New Tenant
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search tenants by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Paper>

      {/* DataTable */}
      <DataTable
        data={filteredTenants}
        columns={columns}
        actions={actions}
        onAction={handleTableAction}
        itemsPerPage={50}
        showPagination={true}
        showExport={true}
        exportFileName="tenants"
        statusOptions={statusOptions}
        getStatusColor={(status) => {
          switch (status) {
            case 'active': return 'success';
            case 'inactive': return 'error';
            case 'suspended': return 'warning';
            default: return 'default';
          }
        }}
        emptyStateIcon={Business}
        emptyStateTitle="No tenants yet"
        emptyStateSubtitle="Tenants will appear here once they register"
        searchEmptyStateTitle="No tenants found matching your search"
        searchEmptyStateSubtitle="Try adjusting your search terms"
        hasSearchResults={!!searchTerm}
      />
          

      {/* Tenant Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {dialogMode === 'create' && 'Create New Tenant'}
          {dialogMode === 'edit' && 'Edit Tenant'}
          {dialogMode === 'view' && 'Tenant Details'}
        </DialogTitle>
        <DialogContent>
          <TenantForm
            tenant={selectedTenant}
            mode={dialogMode}
            onSuccess={() => {
              setDialogOpen(false);
              fetchTenants();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            {dialogMode === 'view' ? 'Close' : 'Cancel'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Tenant Form Component
const TenantForm = ({ tenant, mode, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    website: '',
    description: '',
    status: 'active',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (tenant) {
      setFormData({
        name: tenant.name || '',
        email: tenant.email || '',
        phone: tenant.phone || '',
        address: tenant.address || '',
        website: tenant.website || '',
        description: tenant.description || '',
        status: tenant.status || 'active',
      });
    }
  }, [tenant]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === 'view') return;

    try {
      setLoading(true);
      setError('');

      let response;
      if (mode === 'create') {
        response = await apiService.post('/superadmin/tenants', formData);
      } else {
        response = await apiService.put(`/superadmin/tenants/${tenant.id}`, formData);
      }

      if (response.success) {
        onSuccess();
      } else {
        setError(response.message || 'Failed to save tenant');
      }
    } catch (err) {
      setError('Error saving tenant: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
        <TextField
          label="Tenant Name"
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          required
          disabled={mode === 'view'}
          fullWidth
        />
        <TextField
          label="Email"
          type="email"
          value={formData.email}
          onChange={(e) => handleChange('email', e.target.value)}
          required
          disabled={mode === 'view'}
          fullWidth
        />
        <TextField
          label="Phone"
          value={formData.phone}
          onChange={(e) => handleChange('phone', e.target.value)}
          required
          disabled={mode === 'view'}
          fullWidth
        />
        <TextField
          label="Website"
          value={formData.website}
          onChange={(e) => handleChange('website', e.target.value)}
          disabled={mode === 'view'}
          fullWidth
        />
        <TextField
          label="Address"
          value={formData.address}
          onChange={(e) => handleChange('address', e.target.value)}
          disabled={mode === 'view'}
          fullWidth
          multiline
          rows={2}
        />
        <TextField
          label="Description"
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          disabled={mode === 'view'}
          fullWidth
          multiline
          rows={2}
        />
        <FormControl fullWidth>
          <InputLabel>Status</InputLabel>
          <Select
            value={formData.status}
            label="Status"
            onChange={(e) => handleChange('status', e.target.value)}
            disabled={mode === 'view'}
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {mode !== 'view' && (
        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Saving...' : (mode === 'create' ? 'Create Tenant' : 'Update Tenant')}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default TenantManagement; 